import { useState, useEffect } from "react";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Card } from "./ui/card";
import { Trash, Plus, Sparkle, FloppyDisk, Copy } from "@phosphor-icons/react";
import { toast } from "sonner";

const INCOME_FIELDS = [
    { key: "salary", label: "Salary / wages" },
    { key: "self_employment", label: "Self-employment" },
    { key: "benefits", label: "SSI / disability / unemployment" },
    { key: "other_income", label: "Other income" },
];

const EXPENSE_FIELDS = [
    { key: "housing", label: "Housing (rent/mortgage)" },
    { key: "utilities", label: "Utilities" },
    { key: "food", label: "Food / groceries" },
    { key: "transport", label: "Transportation" },
    { key: "insurance", label: "Insurance (health/auto)" },
    { key: "medical", label: "Medical" },
    { key: "childcare", label: "Childcare / school" },
    { key: "other_exp", label: "Other essential expenses" },
];

const ASSET_FIELDS = [
    { key: "cash", label: "Cash on hand" },
    { key: "checking", label: "Checking account" },
    { key: "savings", label: "Savings" },
    { key: "vehicle_equity", label: "Vehicle equity" },
    { key: "other_assets", label: "Other assets" },
];

export default function HardshipWorksheet({ caseId, initialData, onSaved }) {
    const [income, setIncome] = useState({});
    const [expenses, setExpenses] = useState({});
    const [assets, setAssets] = useState({});
    const [debts, setDebts] = useState([]);
    const [reason, setReason] = useState("");
    const [notes, setNotes] = useState("");
    const [metrics, setMetrics] = useState(null);
    const [narrative, setNarrative] = useState("");
    const [saving, setSaving] = useState(false);
    const [generating, setGenerating] = useState(false);

    useEffect(() => {
        if (!initialData) return;
        setIncome(initialData.monthly_income || {});
        setExpenses(initialData.monthly_expenses || {});
        setAssets(initialData.assets || {});
        setDebts(initialData.debts || []);
        setReason(initialData.hardship_reason || "");
        setNotes(initialData.hardship_notes || "");
        setMetrics(initialData.metrics || null);
        setNarrative(initialData.narrative || "");
    }, [initialData]);

    const setField = (state, setter) => (key) => (e) =>
        setter({ ...state, [key]: e.target.value });

    const addDebt = () => setDebts([...debts, { name: "", balance: "", monthly: "" }]);
    const removeDebt = (i) => setDebts(debts.filter((_, idx) => idx !== i));
    const updateDebt = (i, k, v) => {
        const next = [...debts];
        next[i][k] = v;
        setDebts(next);
    };

    const toNum = (o) => Object.fromEntries(Object.entries(o).map(([k, v]) => [k, parseFloat(v) || 0]));

    const save = async () => {
        setSaving(true);
        try {
            const { data } = await api.patch(`/cases/${caseId}/hardship`, {
                monthly_income: toNum(income),
                monthly_expenses: toNum(expenses),
                assets: toNum(assets),
                debts: debts.map((d) => ({
                    name: d.name || "",
                    balance: parseFloat(d.balance) || 0,
                    monthly: parseFloat(d.monthly) || 0,
                })),
                hardship_reason: reason,
                hardship_notes: notes,
            });
            setMetrics(data.metrics);
            toast.success("Hardship worksheet saved");
            onSaved?.();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };

    const generateNarrative = async () => {
        setGenerating(true);
        try {
            const { data } = await api.post("/hardship/narrative", { case_id: caseId });
            setNarrative(data.narrative);
            toast.success("Hardship narrative drafted");
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setGenerating(false); }
    };

    return (
        <div className="space-y-6" data-testid="hardship-worksheet">
            <p className="text-xs text-slate-500 italic">
                Fill the worksheet, then we'll compute affordability and draft a hardship narrative you can attach to your settlement offer.
            </p>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Section title="Monthly income" fields={INCOME_FIELDS} state={income} setter={setField(income, setIncome)} prefix="inc" />
                <Section title="Monthly essential expenses" fields={EXPENSE_FIELDS} state={expenses} setter={setField(expenses, setExpenses)} prefix="exp" />
                <Section title="Assets" fields={ASSET_FIELDS} state={assets} setter={setField(assets, setAssets)} prefix="ast" />
                <Card className="p-5 space-y-3">
                    <div className="flex items-center justify-between">
                        <h4 className="font-semibold">Outstanding debts</h4>
                        <Button type="button" variant="outline" size="sm" onClick={addDebt} data-testid="hardship-add-debt">
                            <Plus size={14} className="mr-1" /> Add
                        </Button>
                    </div>
                    {debts.length === 0 ? (
                        <p className="text-xs text-slate-500">No debts listed yet.</p>
                    ) : (
                        <div className="space-y-2">
                            {debts.map((d, i) => (
                                <div key={i} className="grid grid-cols-[1fr_120px_120px_auto] gap-2">
                                    <Input data-testid={`debt-name-${i}`} placeholder="Creditor" value={d.name} onChange={(e) => updateDebt(i, "name", e.target.value)} className="h-9" />
                                    <Input data-testid={`debt-balance-${i}`} type="number" placeholder="Balance" value={d.balance} onChange={(e) => updateDebt(i, "balance", e.target.value)} className="h-9 font-mono" />
                                    <Input data-testid={`debt-monthly-${i}`} type="number" placeholder="Min/mo" value={d.monthly} onChange={(e) => updateDebt(i, "monthly", e.target.value)} className="h-9 font-mono" />
                                    <Button type="button" variant="ghost" size="sm" onClick={() => removeDebt(i)}><Trash size={14} /></Button>
                                </div>
                            ))}
                        </div>
                    )}
                </Card>
            </div>

            <Card className="p-5 space-y-3">
                <div>
                    <Label>Reason for hardship</Label>
                    <Input data-testid="hardship-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Medical leave, job loss, divorce, disability" className="mt-1.5 h-10" />
                </div>
                <div>
                    <Label>Additional context (optional)</Label>
                    <Textarea data-testid="hardship-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="mt-1.5" />
                </div>
            </Card>

            <div className="flex gap-3 flex-wrap">
                <Button onClick={save} disabled={saving} data-testid="hardship-save-btn" className="bg-[#0F1E3D] hover:bg-[#1A2C57] h-10">
                    <FloppyDisk size={14} className="mr-1.5" /> {saving ? "Saving…" : "Save worksheet"}
                </Button>
                {metrics && (
                    <Button onClick={generateNarrative} disabled={generating} variant="outline" data-testid="hardship-narrative-btn" className="h-10">
                        <Sparkle size={14} className="mr-1.5" weight="fill" />
                        {generating ? "Drafting…" : "Draft hardship narrative"}
                    </Button>
                )}
            </div>

            {metrics && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-slate-200 border border-slate-200 rounded-md overflow-hidden" data-testid="hardship-metrics">
                    <Metric label="Income (monthly)" value={`$${metrics.income_total.toLocaleString()}`} />
                    <Metric label="Expenses (monthly)" value={`$${metrics.expense_total.toLocaleString()}`} />
                    <Metric label="Disposable" value={`$${metrics.disposable.toLocaleString()}`} tone={metrics.disposable > 0 ? "green" : "red"} />
                    <Metric label="DTI" value={metrics.dti_pct != null ? `${metrics.dti_pct}%` : "—"} />
                    <Metric label="Total debt" value={`$${metrics.debt_total.toLocaleString()}`} />
                    <Metric label="Min monthly debt pmts" value={`$${metrics.monthly_debt_payments.toLocaleString()}`} />
                    <Metric label="Affordable lump (~6mo)" value={`$${metrics.affordable_lump_settlement.toLocaleString()}`} tone="navy" />
                    <Metric label="Affordable monthly plan" value={`$${metrics.affordable_monthly_plan.toLocaleString()}`} tone="navy" />
                </div>
            )}

            {narrative && (
                <Card className="p-5 bg-blue-50/40 border-blue-200" data-testid="hardship-narrative">
                    <div className="flex items-center justify-between">
                        <p className="overline">Hardship narrative</p>
                        <Button variant="ghost" size="sm" onClick={() => { navigator.clipboard.writeText(narrative); toast.success("Copied"); }}><Copy size={14} /></Button>
                    </div>
                    <pre className="whitespace-pre-wrap font-sans text-sm text-slate-800 mt-3 leading-relaxed">{narrative}</pre>
                </Card>
            )}
        </div>
    );
}

function Section({ title, fields, state, setter, prefix }) {
    return (
        <Card className="p-5 space-y-3">
            <h4 className="font-semibold">{title}</h4>
            <div className="grid grid-cols-2 gap-2">
                {fields.map((f) => (
                    <div key={f.key}>
                        <Label className="text-xs">{f.label}</Label>
                        <Input
                            type="number"
                            min="0"
                            step="0.01"
                            data-testid={`${prefix}-${f.key}`}
                            value={state[f.key] ?? ""}
                            onChange={setter(f.key)}
                            className="mt-1 h-9 font-mono"
                        />
                    </div>
                ))}
            </div>
        </Card>
    );
}

function Metric({ label, value, tone }) {
    const tones = {
        green: "bg-emerald-50 text-emerald-900",
        red: "bg-rose-50 text-rose-900",
        navy: "bg-[#0F1E3D] text-white",
    };
    return (
        <div className={`p-4 ${tones[tone] || "bg-white"}`}>
            <p className={`overline ${tone === "navy" ? "text-white/60" : ""}`}>{label}</p>
            <p className="font-serif text-xl mt-1 font-mono">{value}</p>
        </div>
    );
}
